import { Store } from 'unistore';
import EventTarget from '@ungap/event-target';

import { parseAmountValue } from '../utils/parseAmountValue';
import {
  ICheckoutState,
  SceneStage,
  SceneState,
  BaseStore,
  NodeLike,
} from './BaseStore';

import {
  BusinessDetails,
  CustomerDetails,
  OrderDetails,
  PaymentMethodToken,
  PurchaseInformation,
  SupportedLocale,
  VaultListItem,
} from '../types';
import { TranslationUnit } from '../assets/translations/Translations';
import { formatVaultedToken } from '../checkout/formatVaultedToken';
import { BasePaymentMethod } from '../payment-methods/BasePaymentMethod';
import { Vault } from '../checkout/Vault';
import { CardMetadata } from '../hosted-scripts/CardMetadata';
import { PaymentMethodType } from '../enums/Tokens';
import { IOptionStoreSetter, IOptionStoreListener } from './OptionStore';
import StoreChangeEventHandler from './StoreChangeEventHandler';
import { totalAmountFromItems } from '../utils/totalAmountCalculator';
import { ErrorCode, PrimerClientError } from '../errors';

export default class CheckoutStore
  extends BaseStore
  implements IOptionStoreSetter, IOptionStoreListener {
  private submitButtonEventListener: EventTarget;

  private vault: Vault | null;

  private eventHandlers: Record<string, StoreChangeEventHandler>;

  constructor(state: Store<ICheckoutState>) {
    super(state);

    this.submitButtonEventListener = new EventTarget();
    this.vault = null;

    this.eventHandlers = {
      'options.purchaseInfo': new StoreChangeEventHandler(
        this,
        (store) => store.getState().options.orderDetails,
      ),
      'options.customerDetails': new StoreChangeEventHandler(
        this,
        (store) => store.getState().options.customerDetails,
      ),
      'options.businessDetails': new StoreChangeEventHandler(
        this,
        (store) => store.getState().options.businessDetails,
      ),
      'options.orderDetails': new StoreChangeEventHandler(
        this,
        (store) => store.getState().options.orderDetails,
      ),
    };
  }

  setVault(vault: Vault) {
    this.vault = vault;
  }

  setIsLoading(isLoading: boolean): void {
    this.produceState((draft) => {
      draft.isLoading = isLoading;
    });
  }

  setCardMetadata(meta: CardMetadata): void {
    this.produceState((draft) => {
      draft.card.metadata = meta;
    });
  }

  setIsProcessing(isProcessing: boolean): void {
    this.produceState((draft) => {
      draft.isProcessing = isProcessing;
    });
  }

  setLocale(locale: SupportedLocale): void {
    this.produceState((draft) => {
      draft.locale = locale;
    });
  }

  ///////////////////////////////////////////
  // Scene
  ///////////////////////////////////////////

  setScene(scene: string): void {
    this.produceState((draft) => {
      let sceneState = draft.sceneStates[scene];

      if (!sceneState) {
        sceneState = { stage: SceneStage.Init };
        draft.sceneStates[scene] = sceneState;
      }

      const currentScene = Object.entries(draft.sceneStates).find(
        ([, state]) => state?.stage === SceneStage.Entered,
      )?.[1];

      if (sceneState.stage === SceneStage.Init) {
        sceneState.stage = SceneStage.Mounting;
      } else if (sceneState.stage === SceneStage.Exited) {
        sceneState.stage = SceneStage.Entering;
        if (currentScene) {
          currentScene.stage = SceneStage.Exiting;
        }
      }
    });
  }

  handleSceneEntered(scene: string): void {
    this.produceState((draft) => {
      const sceneState = draft.sceneStates[scene] as SceneState;
      sceneState.stage = SceneStage.Entered;
    });
  }

  handleSceneExited(scene: string): void {
    this.produceState((draft) => {
      const sceneState = draft.sceneStates[scene] as SceneState;
      sceneState.stage = SceneStage.Exited;
    });
  }

  handleSceneMounted(scene: string): void {
    this.produceState((draft) => {
      const currentScene = Object.entries(draft.sceneStates).find(
        ([, sceneState]) => sceneState?.stage === SceneStage.Entered,
      )?.[1];

      const sceneState = draft.sceneStates[scene] as SceneState;
      sceneState.stage = SceneStage.Entering;

      if (currentScene) {
        currentScene.stage = SceneStage.Exiting;
      }
    });
  }

  ///////////////////////////////////////////
  // Token
  ///////////////////////////////////////////

  setCurrentToken(token: PaymentMethodToken | null) {
    this.produceState((draft) => {
      draft.currentToken = token;
    });
  }

  ///////////////////////////////////////////
  // Submit Button
  ///////////////////////////////////////////

  triggerSubmitButtonClick() {
    this.submitButtonEventListener.dispatchEvent(new Event('click'));
    this.setErrorMessage(null);
  }

  addSubmitButtonClickListener(
    listener: EventListener | EventListenerObject | null,
  ) {
    this.submitButtonEventListener.addEventListener('click', listener);
  }

  removeSubmitButtonClickListener(
    listener: EventListener | EventListenerObject | null,
  ) {
    this.submitButtonEventListener.removeEventListener('click', listener);
  }

  setSubmitButtonDisabled(disabled: boolean): void {
    this.produceState((draft) => {
      draft.submitButton.isDisabled = disabled;
    });
  }

  setSubmitButtonVisible(isVisible: boolean): void {
    this.produceState((draft) => {
      draft.submitButton.isVisible = isVisible;
    });
  }

  setSubmitButtonContent(content: string): void {
    this.produceState((draft) => {
      draft.submitButton.message = content;
    });
  }

  ///////////////////////////////////////////
  // Error Message
  ///////////////////////////////////////////

  setErrorMessage(content: string | null): void {
    this.produceState((draft) => {
      draft.error.message = content;
    });
  }

  ///////////////////////////////////////////
  // Payment Methods
  ///////////////////////////////////////////

  addAPM(apm: NodeLike): void {
    this.produceState((draft) => {
      draft.apms.items.push(apm);
    });
  }

  setPaymentMethods(paymentMethods: Record<string, BasePaymentMethod>) {
    this.setState({ paymentMethods });

    const apms = Object.keys(paymentMethods)
      .filter((name) => !/^(?:card|directDebit)$/.test(name))
      .map((name) => ({ id: `primer-checkout-apm-${name}` }))
      .filter(Boolean);

    apms.forEach((apm) => {
      this.addAPM(apm);
    });
  }

  ///////////////////////////////////////////
  // Checkout Modules
  ///////////////////////////////////////////

  setCheckoutModules(checkoutModules: any) {
    this.produceState((draft) => {
      draft.checkoutModules = checkoutModules;
    });
  }

  ///////////////////////////////////////////
  // Vault
  ///////////////////////////////////////////
  selectFirstVault(): void {
    if (this.hasVault) {
      this.produceState((draft) => {
        draft.vault.selected = draft.vault.items[0].id;
      });
    }
  }

  addVault(vault: PaymentMethodToken): Nullable<VaultListItem> {
    const formatted = formatVaultedToken(vault);

    if (formatted == null) {
      return null;
    }

    this.produceState((draft) => {
      draft.isLoading = true;
      draft.tokens.push(vault);
      draft.vault.items.push(formatted);
    });

    return formatted;
  }

  selectVault(id: Nullable<string>): void {
    this.produceState((draft) => {
      draft.vault.selected = id;
    });
  }

  async removeVault(id: string): Promise<void> {
    await this.vault?.delete(id);

    this.produceState((draft) => {
      if (draft.vault.selected === id) {
        draft.vault.selected = null;
      }

      draft.vault.items = draft.vault.items.filter((elm) => elm.id !== id);
      draft.tokens = draft.tokens.filter((elm) => elm.token !== id);
    });
  }

  ///////////////////////////////////////////
  // Small Print
  ///////////////////////////////////////////

  setSmallPrintMessage(message: Nullable<string>): void {
    this.produceState((draft) => {
      draft.smallPrint.message = message;
    });
  }

  ///////////////////////////////////////////
  // Options
  ///////////////////////////////////////////

  setPurchaseInfo(purchaseInfo: Nullable<PurchaseInformation>) {
    if (!purchaseInfo?.totalAmount) {
      return;
    }
    const purchaseInformation = purchaseInfo;
    const { orderDetails } = this.getOrderDetails();
    const updatedOrderDetails = {
      ...orderDetails,
      totalAmount:
        (purchaseInformation as PurchaseInformation).totalAmount.value ?? 0,
      currencyCode: purchaseInformation.totalAmount.currency,
    };
    delete updatedOrderDetails.items;
    delete updatedOrderDetails.totalTaxAmount;
    this.setOrderDetails(updatedOrderDetails, 'CLIENT');
  }

  setCustomerDetails(
    customerDetails: Nullable<CustomerDetails>,
    orderDetailsInitiator: string | null,
  ) {
    this.produceState((draft) => {
      draft.options.customerDetails = customerDetails;
      draft.options.orderDetailsInitiator = orderDetailsInitiator;
    });
  }

  setBusinessDetails(
    businessDetails: Nullable<BusinessDetails>,
    orderDetailsInitiator: string | null,
  ) {
    this.produceState((draft) => {
      draft.options.businessDetails = businessDetails;
      draft.options.orderDetailsInitiator = orderDetailsInitiator;
    });
  }

  setOrderDetails(
    orderDetails: Nullable<OrderDetails>,
    orderDetailsInitiator: string | null,
  ) {
    if (orderDetails?.items && orderDetails.items.length > 0) {
      const amountFromItems = totalAmountFromItems(
        orderDetails?.items,
        orderDetails?.shippingAmount,
        orderDetails?.totalTaxAmount,
      );
      if (
        parseAmountValue(orderDetails.totalAmount ?? 0).asNumber() &&
        amountFromItems > 0 &&
        parseAmountValue(orderDetails.totalAmount ?? 0).asNumber() !==
          amountFromItems
      ) {
        throw PrimerClientError.fromErrorCode(ErrorCode.VALIDATION_ERROR, {
          message:
            'There are discrepancies between totalAmount and the items provided',
        });
      }
    }
    this.produceState((draft) => {
      draft.options.orderDetails = orderDetails;
      draft.options.orderDetailsInitiator = orderDetailsInitiator;
    });
  }

  getPurchaseInfo() {
    return this.getState().options.purchaseInfo;
  }

  getCustomerDetails() {
    return this.getState().options.customerDetails;
  }

  getBusinessDetails() {
    return this.getState().options.businessDetails;
  }

  getOrderDetails() {
    return {
      orderDetails: this.getState().options.orderDetails,
      orderDetailsInitiator: this.getState().options.orderDetailsInitiator,
    };
  }

  on(eventName: string, listener: () => void) {
    const eventHandler = this.eventHandlers[eventName];
    if (!eventHandler) {
      return;
    }

    eventHandler.subscribe(listener);
  }

  off(eventName: string, listener: () => void) {
    const eventHandler = this.eventHandlers[eventName];
    if (!eventHandler) {
      return;
    }

    eventHandler.unsubscribe(listener);
  }

  ///////////////////////////////////////////
  // Payment Method Popup Overlay
  ///////////////////////////////////////////

  showPaymentMethodPopupOverlay({
    paymentMethodName,
    logoSrc,
    logoAltLabel,
    onCloseClick,
    onFocusClick,
  }: {
    paymentMethodName?: string;
    logoSrc?: string;
    logoAltLabel?: string;

    onCloseClick: () => void;
    onFocusClick: () => void;
  }) {
    this.produceState((draft) => {
      draft.paymentMethodPopupOverlay.isVisible = true;
      draft.paymentMethodPopupOverlay.paymentMethodName = paymentMethodName;
      draft.paymentMethodPopupOverlay.logoSrc = logoSrc;
      draft.paymentMethodPopupOverlay.logoAltLabel = logoAltLabel;
      draft.paymentMethodPopupOverlay.onCloseClick = onCloseClick;
      draft.paymentMethodPopupOverlay.onFocusClick = onFocusClick;
    });
  }

  hidePaymentMethodPopupOverlay() {
    this.produceState((draft) => {
      draft.paymentMethodPopupOverlay.isVisible = false;
    });
  }

  ///////////////////////////////////////////
  // Getters
  ///////////////////////////////////////////

  get hasAPMs(): boolean {
    return false;
  }

  get selectedAPM(): Nullable<string> {
    return this.getState().apms.selected;
  }

  get hasVault(): boolean {
    return this.getState().vault.items.length > 0;
  }

  get hasCard(): boolean {
    return (
      Object.values(this.getState().paymentMethods).findIndex(
        ({ type }) => type === PaymentMethodType.PAYMENT_CARD,
      ) !== -1
    );
  }

  get hasDirectDebit(): boolean {
    return (
      Object.values(this.getState().paymentMethods).findIndex(
        ({ type }) => type === PaymentMethodType.GO_CARDLESS,
      ) !== -1
    );
  }

  get selectedVault(): Nullable<string> {
    return this.getState().vault.selected;
  }

  get savePaymentMethodVisible(): boolean {
    return this.getState().options.showSavePaymentMethod;
  }

  getSelectedVaultToken(): Nullable<PaymentMethodToken> {
    return (
      this.getState().tokens.find(
        (elm) => elm.token === this.getState().vault.selected,
      ) || null
    );
  }

  getAllAPMs(): NodeLike[] {
    return this.getState().apms.items;
  }

  getAllVaulted(): VaultListItem[] {
    return this.getState().vault.items;
  }

  getTranslations(): TranslationUnit {
    return this.getState().translation as Record<string, string>;
  }

  hasVaultedToken(token: PaymentMethodToken): boolean {
    return !!this.getState().tokens.find(
      (elm) => elm.analyticsId === token.analyticsId,
    );
  }
}
